/**
 * Quick drug price lookup widget — self-contained.
 * Lets staff look up the price of a product × quantity without creating an order.
 */
import { useState } from 'react'

import {
  formatRegionPrice,
  type Product,
  type ProductDerivedFields,
  REGIONS_BY_ID,
} from '@snootsvet/shared'

import { Card, CardContent } from '@/frontend/components/ui/card'
import { Input } from '@/frontend/components/ui/input'
import { ProductSearch } from '@/frontend/features/inventory/product-search'
import { useViewerRegion } from '@/frontend/features/region/region-context'

/** Quick drug price lookup widget — self-contained. */
export function PriceEstimateWidget() {
  const { region } = useViewerRegion()
  const [product, setProduct] = useState<(Product & ProductDerivedFields) | null>(null)
  const [qty, setQty] = useState('')

  const total = product && qty ? product.unitPrice * Number.parseInt(qty, 10) : null

  return (
    <Card size="sm" className="min-w-sm shrink-0 flex-1 max-w-xl">
      <CardContent>
        <p className="mb-2 text-sm text-muted-foreground">Price Estimate</p>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <ProductSearch id="estimate-drug" onChange={setProduct} placeholder="Search drug..." />
          </div>
          <Input
            id="estimate-qty"
            className="w-24"
            type="number"
            min={1}
            placeholder="Qty"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <p className="min-w-18 text-center font-semibold whitespace-nowrap">
            {total != null
              ? formatRegionPrice(total, product?.region ?? region)
              : `${REGIONS_BY_ID[region].currencySymbol}-.--`}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
